"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldAlert, Layers, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function Arenas() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1
    },
    whileInView: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="inflatablesafetyarenasguide" data-nav="light" className="bg-background text-foreground py-12 md:py-16 border-b border-border">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6">
        <motion.div {...animationProps}>
          <Card className="rounded-none border border-border bg-card text-card-foreground shadow-xs p-0 gap-0">
            {/* Technical Specification Eyebrow Bar */}
            <div className="border-b border-border bg-muted/60 px-6 py-2.5 flex items-center justify-between">
              <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase flex items-center gap-2">
                <Layers className="size-3.5 text-primary shrink-0" />
                EQUIPMENT SPECIFICATION / ARENA INTEGRATION
              </span>
              <span className="font-mono text-[11px] text-muted-foreground hidden sm:inline-block">
                REF: DOC-ARENA-SPEC
              </span>
            </div>

            <CardHeader className="p-6 sm:p-8 pb-4">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex size-10 items-center justify-center border border-border bg-muted shrink-0 text-primary">
                  <ShieldAlert className="size-5" />
                </div>
                <div className="space-y-1">
                  <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold uppercase tracking-tight text-foreground">
                    Mechanical Bulls With Inflatable Safety Arenas
                  </h2>
                  <p className="font-mono text-xs text-muted-foreground uppercase tracking-wide">
                    Operational Evaluation &amp; Equipment Interface Protocol
                  </p>
                </div>
              </div>
            </CardHeader>

            <Separator className="bg-border" />

            <CardContent className="p-6 sm:p-8 space-y-6">
              {/* Structured Industrial Technical Paragraphs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="border border-border/70 p-5 bg-background/50 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-[11px] font-semibold text-primary uppercase">
                        Section 01 // System Architecture
                      </span>
                      <CheckCircle2 className="size-4 text-muted-foreground/60" />
                    </div>
                    <p className="text-sm font-sans text-foreground/90 leading-relaxed">
                      Many mechanical bull systems use an inflatable safety arena around the operating area. However, the bull and inflatable should still be evaluated as separate parts of the package.
                    </p>
                  </div>
                </div>

                <div className="border border-border/70 p-5 bg-background/50 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-[11px] font-semibold text-primary uppercase">
                        Section 02 // Pre-Purchase Check
                      </span>
                      <CheckCircle2 className="size-4 text-muted-foreground/60" />
                    </div>
                    <p className="text-sm font-sans text-foreground/90 leading-relaxed">
                      Before purchasing, confirm whether the inflatable is included, its dimensions where documented, whether the blower is supplied and how the bull base integrates with the arena.
                    </p>
                  </div>
                </div>

                <div className="border border-border/70 p-5 bg-background/50 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-[11px] font-semibold text-primary uppercase">
                        Section 03 // Secondary Inspection
                      </span>
                      <AlertTriangle className="size-4 text-muted-foreground/60" />
                    </div>
                    <p className="text-sm font-sans text-foreground/90 leading-relaxed">
                      For used equipment, check the available information about patches, previous repairs, wear and the condition of the blower.
                    </p>
                  </div>
                </div>
              </div>

              {/* Metal Rating Plate Frame Callout */}
              <div className="border border-border bg-muted p-5 sm:p-6 rounded-none relative overflow-hidden">
                <div className="flex items-start gap-4">
                  <div className="hidden sm:block font-mono text-xs uppercase text-muted-foreground tracking-widest pt-0.5 whitespace-nowrap select-none">
                    [NOTICE]
                  </div>
                  <div className="space-y-1">
                    <p className="font-sans text-sm sm:text-base font-medium text-foreground leading-normal">
                      Mechanical Bull Shop also has a dedicated{" "}
                      <Link to="/InflatableMechanicalBullsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
                        Inflatable Mechanical Bulls page
                      </Link>{" "}
                      for buyers comparing complete bull-and-inflatable systems.
                    </p>
                    <p className="font-mono text-[11px] text-muted-foreground">
                      Informational advisory for equipment acquisition and system comparison.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}